import React, { useCallback, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"

import { saveAs } from "file-saver"
import JSZip from "jszip"
import JSZipUtils from "jszip-utils"
import { Modal } from "antd"

import Button from "../../components/button"
import api from "../../api"
import { useLanguage } from "@languages/index"
import { getServiceNameEn } from "constants/service/service"

// Props Interface
interface Props {
  isVisible: boolean
  letClose: () => void
  makeFetchParams: () => any
}

const ModalPostingImagesDownloader = (props: Props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.postings }
  const [isWorking, setIsWorking] = useState(false)
  const [postingsCount, setPostingsCount] = useState(null)
  const [progressDownload, setProgressDownload] = useState(0)
  const intrinsicProgressCompress = useRef(0)
  const [progressCompress, setProgressCompress] = useState(0)
  const [modalAlertIsVisible, setModalAlertIsVisible] = useState(false)
  const alertContent = useRef({
    title: null,
    text: languageTexts.modal_downloader_text_1,
  })

  async function fetchPostingsToDownload() {
    const postings_to_download = []
    try {
      const params = props.makeFetchParams()
      params["size"] = -1 // all postings with params
      params["select"] = ["id", "user_id"]

      setIsWorking(true)
      const response = await api.Post.index(params)
      if (response?.data?.postings == null || response.data.postings.length === 0)
        return [null, languageTexts.modal_downloader_text_2]
      if (response.data.postings.length > 100) {
        return [null, languageTexts._modal_downloader_text_3(response.data.postings.length)]
      }
      setPostingsCount(response.data.postings.length)
      for (let i = 0; i < response["data"]["postings"].length; i++) {
        const posting = response["data"]["postings"][i]
        let posting_to_download = {
          filename: `${posting["user"]["username"]}_${posting["userModel"]["height"]}cm_${posting["id"]}.zip`,
          image_urls: [],
        }
        for (const image of posting["images"]) {
          if (image["originalUrl"].indexOf("video-thumbnail-brand") >= 0) continue
          posting_to_download["image_urls"].push(image["originalUrl"])
        }
        if (posting_to_download["image_urls"].length === 0) continue
        postings_to_download.push(posting_to_download)
      }
      return [postings_to_download, "ok"]
    } catch (e) {
      return [null, e.message]
    }
  }

  async function downloadImagesAsZipData(image_urls) {
    const zip = new JSZip()
    for (const image_url of image_urls) {
      const filename = image_url.split("/").splice(-1)[0]
      const image_data = await new Promise<any>((resolve, reject) => {
        JSZipUtils.getBinaryContent(image_url, function (err, data) {
          if (err) {
            reject(err) // or handle the error
          }
          resolve(data)
        })
      })
      zip.file(filename, image_data, {
        base64: true,
        binary: true,
        compression: "STORE",
      })
    }

    const zip_file_data = await zip.generateAsync(
      {
        type: "blob",
        compression: "STORE",
      }
      // function updateCallback (metadata) {
      //   console.log("progression: " + metadata.percent.toFixed(2) + " %");
      //   if(metadata.currentFile) {
      //       console.log("current file = " + metadata.currentFile);
      //   }
      // }
    )
    return zip_file_data
  }

  async function downloadFiles(event) {
    const [postings, error_message] = await fetchPostingsToDownload()
    if (postings == null) {
      intrinsicProgressCompress.current = 0
      setPostingsCount(null)
      setProgressCompress(0)
      setProgressDownload(0)
      setIsWorking(false)
      alertContent.current["text"] = error_message
      setModalAlertIsVisible(true)
      return
    }

    var zip = new JSZip()
    for (let i = 0; i < postings.length; i++) {
      setProgressDownload(i + 1)
      const posting = postings[i]
      const posting_file_name = posting["filename"]
      const posting_file_data = await downloadImagesAsZipData(posting["image_urls"])
      zip.file(posting_file_name, posting_file_data, {
        base64: true,
        binary: true,
        compression: "STORE",
      })
    }

    const zip_file_data = await zip.generateAsync(
      {
        type: "blob",
        compression: "STORE",
      },
      function updateCallback(metadata) {
        if (metadata.percent > intrinsicProgressCompress.current + 5 || metadata.percent == 100) {
          intrinsicProgressCompress.current = metadata.percent
          setProgressCompress(metadata.percent)
          // console.log("progression: " + metadata.percent.toFixed(2) + " %");
          // if(metadata.currentFile) {
          //     console.log("current file = " + metadata.currentFile);
          // }
        }
      }
    )

    saveAs(zip_file_data, `${getServiceNameEn()}_postings.zip`)

    await new Promise((resolve) => setTimeout(() => resolve(true), 5000))
    setIsWorking(false)
  }

  const AlertModal = (props) => {
    return (
      <Modal
        className="rounded-antd-modal"
        centered={true}
        open={props.isVisible}
        onOk={props.letClose}
        onCancel={props.letClose}
        footer={null}
        closable={false}
        zIndex={1010}
        width={320}
      >
        <div className="flex flex-col justify-center align-center p-5 bg-white rounded-md">
          <div className="content flex flex-col">
            <span className="font-bold text-[22px] text-black">{alertContent.current["title"]}</span>
            <div className="flex flex-col items-start py-4">
              <span className="prose prose-md whitespace-pre-wrap leading-5 text-black text-14px">
                {alertContent.current["text"]}
              </span>
            </div>
          </div>
          <div className="buttons flex flex-col rows-2 mt-3 gap-1.5">
            <Button
              className={"min-w-[8rem] py-3 flex justify-center text-center text-base"}
              textColor={"WHITE"}
              buttonColor={"BLACK"}
              text={languageTexts.confirm}
              onClick={() => {
                props.letClose()
              }}
            />
          </div>
        </div>
      </Modal>
    )
  }

  function makeParamsText(params) {
    let text = ""
    const params_keys = Object.keys(params)
    for (let key of params_keys) {
      if (params[key] == null || params[key] === "") continue
      switch (key) {
        case "type":
          if (params[key] === "SEEDING") text += languageTexts.modal_downloader_text_4
          if (params[key] === "RENTAL") text += languageTexts.modal_downloader_text_5
        case "startDt":
          text += languageTexts._modal_downloader_text_6(params[key])
          break
        case "endDt":
          text += languageTexts._modal_downloader_text_7(params[key])
          break
        case "name":
          if (params_keys.indexOf("userId") >= 0 && params["userId"] != null)
            text += languageTexts._modal_downloader_text_8(params[key])
          else if (params_keys.indexOf("productMasterId") >= 0 && params["productMasterId"] != null)
            text += languageTexts._modal_downloader_text_9(params[key])
          break
        case "searchText":
          if (params_keys.indexOf("productMasterId") >= 0 && params["productMasterId"] != null)
            text += languageTexts._modal_downloader_text_10(params[key])
          else text += languageTexts._modal_downloader_text_11(params[key])
          break
      }
    }
    return text
  }

  const current_params_text = makeParamsText(props.makeFetchParams())
  return (
    <>
      <Modal
        className="rounded-antd-modal"
        centered={true}
        open={props.isVisible}
        onOk={props.letClose}
        onCancel={props.letClose}
        footer={null}
        closable={false}
        maskClosable={false}
        // zIndex={1010}
        width={360}
      >
        <div className="flex flex-col justify-center align-center p-5 bg-white rounded-md select-none">
          <div className="content flex flex-col">
            <span className="font-bold text-[22px] text-black mt-[14px] pb-2 border-b border-b-slate-400">
              {languageTexts.modal_downloader_text_12}
            </span>
            <div className="flex flex-col items-start mt-[18px]">
              {!isWorking ? (
                <>
                  {current_params_text === "" ? null : (
                    <div className="pl-[6px]">
                      <div className="text-[13px] font-bold pb-1">{languageTexts.modal_downloader_text_13}</div>
                      <div className="text-[13px] whitespace-pre-wrap pb-3">{current_params_text}</div>
                    </div>
                  )}
                  <div className="text-[13px] pl-[14px] -indent-[9px]">{languageTexts.modal_downloader_text_14}</div>
                  <div className="text-[13px] pl-[14px] -indent-[9px]">{languageTexts.modal_downloader_text_15}</div>
                  <Button
                    className={"min-w-[10rem] py-3 flex justify-center text-center text-base mt-[14px]"}
                    textColor={"WHITE"}
                    buttonColor={"BLACK"}
                    text={languageTexts.start2}
                    onClick={(e) => downloadFiles(e)}
                  />
                </>
              ) : progressCompress === 0 ? (
                postingsCount == null ? (
                  <div className="w-full pb-4 mt-[14px]">
                    <div className="ml-[10px] text-[#666] font-bold text-[13px]">
                      {languageTexts.modal_downloader_text_16}
                    </div>
                  </div>
                ) : (
                  <div className="w-full pb-5 mt-[14px]">
                    <div className="ml-[10px] text-[#666] font-bold text-[13px]">
                      {languageTexts._modal_downloader_text_17(((progressDownload / postingsCount) * 100).toFixed(2))}
                    </div>
                    <div className="relative h-5 mt-[10px] rounded-md overflow-hidden bg-gray-300">
                      <div
                        className="absolute top-0 bottom-0 left-0 rounded-md bg-gradient-to-r from-gray-600 to-black"
                        style={{
                          width: `${(progressDownload / postingsCount) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full pb-5 mt-[14px]">
                  {progressCompress === 100 ? (
                    <div className="ml-[10px] text-[#666] font-bold text-[13px]">
                      {languageTexts.modal_downloader_text_18}
                    </div>
                  ) : (
                    <div className="ml-[10px] text-[#666] font-bold text-[13px]">
                      {languageTexts._modal_downloader_text_19(progressCompress.toFixed(2))}
                    </div>
                  )}
                  <div className="relative h-5 mt-[10px] rounded-md overflow-hidden bg-gray-300">
                    <div
                      className="absolute top-0 bottom-0 left-0 rounded-md bg-gradient-to-r from-gray-600 to-black"
                      style={{ width: `${progressCompress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CLOSE BUTTON */}
          {isWorking ? null : (
            <div className="buttons flex flex-col rows-2 gap-1.5 mt-[6px]">
              <Button
                className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
                textColor={"WHITE"}
                buttonColor={"BLACK"}
                text={languageTexts.close}
                onClick={() => {
                  if (
                    postingsCount != null &&
                    progressDownload !== 0 &&
                    progressCompress !== 0 &&
                    progressCompress !== 100
                  ) {
                    alertContent.current["text"] = languageTexts.modal_downloader_text_20
                    setModalAlertIsVisible(true)
                    return
                  }
                  // reset
                  intrinsicProgressCompress.current = 0
                  setPostingsCount(null)
                  setProgressCompress(0)
                  setProgressDownload(0)
                  setIsWorking(false)
                  props.letClose()
                }}
              />
            </div>
          )}
        </div>
      </Modal>
      <AlertModal isVisible={modalAlertIsVisible} letClose={(e) => setModalAlertIsVisible(false)} />
    </>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(ModalPostingImagesDownloader)
